/**
 * Targeting V2 — columns, measures, group-by, and ComplexFilter configs.
 */

import {
  commonFilterOperators,
  updatePlaceholders,
} from "../../components/Filter/FilterData/quadrantFilter";

export const TARGETING_V2_DEFAULT_DIMENSIONS = [
  "targeting",
  "targeting_type",
  "match_type",
  "campaign_name",
  "campaign_id",
  "ad_group_id",
  "target_id",
];

/** Always fetched as measures (not group-by dims). */
export const TARGETING_V2_ALWAYS_MEASURES = [
  "bid",
  "ad_group_name",
  "portfolio_name",
  "campaign_format",
  "campaign_type",
  // "campaign_status",
  "campaign_bid_strategy",
  "product_set",
  "status",
];

export const TARGETING_V2_PAGE_MEASURES = [
  "spends",
  "impressions",
  "clicks",
  "orders",
  "revenue",
  "roas",
  "ctr",
  "cpc",
  "cvr",
  "acos",
];

/** Metrics that get Current / Previous columns in comparison view. */
export const TARGETING_V2_COMPARISON_METRIC_KEYS = [
  "spends",
  "impressions",
  "clicks",
  "orders",
  "revenue",
  "roas",
  "ctr",
  "cpc",
  "cvr",
  "acos",
];

export const TARGETING_V2_GROUP_BY_OPTIONS = [
  {
    label: "Targeting",
    value: "targeting",
    dimensions: TARGETING_V2_DEFAULT_DIMENSIONS,
  },
];

export const TARGETING_V2_COLUMN_CONFIG = [
  {
    key: "status",
    title: "Status",
    shortTitle: "Status",
    type: "string",
    dataIndex: "status",
    minWidth: 100,
    alignment: "left",
    isSticky: true,
    isSingleColumn: true,
    isAttribute: true,
  },
  {
    key: "targeting",
    title: "Targeting",
    shortTitle: "Targeting",
    type: "string",
    dataIndex: "targeting",
    isDimension: true,
    isSticky: true,
    minWidth: 220,
    alignment: "left",
    isSingleColumn: true,
  },
  {
    key: "campaign_id",
    innerKeys: {
      productName: "campaign_name",
      asin: { label: "Campaign ID", value: "campaign_id" },
    },
    title: "Campaign",
    shortTitle: "Campaign Name",
    type: "string",
    dataIndex: "campaign_id",
    isDimension: true,
    isSticky: true,
    minWidth: 240,
    alignment: "left",
    isSingleColumn: true,
  },
  {
    key: "ad_group_id",
    innerKeys: {
      productName: "ad_group_name",
      asin: { label: "Ad Group ID", value: "ad_group_id" },
    },
    title: "Ad Group",
    shortTitle: "Ad Group",
    type: "string",
    dataIndex: "ad_group_id",
    isDimension: true,
    isSticky: false,
    minWidth: 200,
    alignment: "left",
    isSingleColumn: true,
  },
  {
    key: "portfolio_name",
    title: "Portfolio",
    shortTitle: "Portfolio",
    type: "string",
    dataIndex: "portfolio_name",
    minWidth: 140,
    alignment: "left",
    isSingleColumn: true,
    isAttribute: true,
  },
  {
    key: "product_set",
    title: "Product Set",
    shortTitle: "Product Set",
    type: "string",
    dataIndex: "product_set",
    minWidth: 140,
    alignment: "left",
    isSingleColumn: true,
    isAttribute: true,
  },
  {
    key: "targeting_type",
    title: "Targeting Type",
    shortTitle: "Targeting Type",
    type: "string",
    dataIndex: "targeting_type",
    isDimension: true,
    minWidth: 130,
    alignment: "left",
    isSingleColumn: true,
  },
  {
    key: "match_type",
    title: "Match Type",
    shortTitle: "Match Type",
    type: "string",
    dataIndex: "match_type",
    isDimension: true,
    minWidth: 110,
    alignment: "left",
    isSingleColumn: true,
  },
  {
    key: "campaign_type",
    title: "Campaign Type",
    shortTitle: "Campaign Type",
    type: "string",
    dataIndex: "campaign_type",
    minWidth: 120,
    alignment: "left",
    isSingleColumn: true,
    isAttribute: true,
  },
  {
    key: "campaign_format",
    title: "Campaign Format",
    shortTitle: "Campaign Format",
    type: "string",
    dataIndex: "campaign_format",
    minWidth: 130,
    alignment: "left",
    isSingleColumn: true,
    isAttribute: true,
  },
  {
    key: "campaign_bid_strategy",
    title: "Bid Strategy",
    shortTitle: "Bid Strategy",
    type: "string",
    dataIndex: "campaign_bid_strategy",
    minWidth: 120,
    alignment: "left",
    isSingleColumn: true,
    isAttribute: true,
  },
  {
    key: "bid",
    title: "Current Bid",
    shortTitle: "Current Bid",
    type: "number",
    dataIndex: "bid",
    isCurrencyFormat: true,
    maximumFractionDigits: 2,
    minWidth: 140,
    alignment: "right",
    isEditable: false,
    isSingleColumn: true,
    viewType: ["PERFORMANCE", "FUNNEL"],
  },
  {
    key: "spends",
    title: "Spends",
    shortTitle: "Spends",
    type: "number",
    dataIndex: "spends",
    isCurrencyFormat: true,
    maximumFractionDigits: 0,
    minWidth: 110,
    alignment: "right",
    isComparisonMetric: true,
    viewType: ["PERFORMANCE", "FUNNEL"],
  },
  {
    key: "impressions",
    title: "Impressions",
    shortTitle: "Impressions",
    type: "number",
    dataIndex: "impressions",
    maximumFractionDigits: 0,
    minWidth: 120,
    alignment: "right",
    isComparisonMetric: true,
    viewType: ["PERFORMANCE", "FUNNEL"],
  },
  {
    key: "clicks",
    title: "Clicks",
    shortTitle: "Clicks",
    type: "number",
    dataIndex: "clicks",
    maximumFractionDigits: 0,
    minWidth: 100,
    alignment: "right",
    isComparisonMetric: true,
    viewType: ["PERFORMANCE", "FUNNEL"],
  },
  {
    key: "orders",
    title: "Orders",
    shortTitle: "Orders",
    type: "number",
    dataIndex: "orders",
    maximumFractionDigits: 0,
    minWidth: 100,
    alignment: "right",
    isComparisonMetric: true,
    viewType: ["PERFORMANCE", "FUNNEL"],
  },
  {
    key: "revenue",
    title: "Revenue",
    shortTitle: "Revenue",
    type: "number",
    dataIndex: "revenue",
    isCurrencyFormat: true,
    maximumFractionDigits: 0,
    minWidth: 110,
    alignment: "right",
    isComparisonMetric: true,
    viewType: ["PERFORMANCE", "FUNNEL"],
  },
  {
    key: "roas",
    title: "ROAS",
    shortTitle: "ROAS",
    type: "number",
    dataIndex: "roas",
    maximumFractionDigits: 2,
    minWidth: 90,
    alignment: "right",
    isComparisonMetric: true,
    viewType: ["PERFORMANCE", "FUNNEL"],
  },
  {
    key: "ctr",
    title: "CTR",
    shortTitle: "CTR",
    type: "number",
    dataIndex: "ctr",
    maximumFractionDigits: 2,
    minWidth: 90,
    alignment: "right",
    format: "percent",
    isComparisonMetric: true,
    viewType: ["PERFORMANCE", "FUNNEL"],
  },
  {
    key: "cpc",
    title: "CPC",
    shortTitle: "CPC",
    type: "number",
    dataIndex: "cpc",
    isCurrencyFormat: true,
    maximumFractionDigits: 2,
    minWidth: 90,
    alignment: "right",
    isComparisonMetric: true,
    viewType: ["PERFORMANCE"],
  },
  {
    key: "cvr",
    title: "CVR",
    shortTitle: "CVR",
    type: "number",
    dataIndex: "cvr",
    maximumFractionDigits: 2,
    minWidth: 90,
    alignment: "right",
    isComparisonMetric: true,
    viewType: ["FUNNEL"],
  },
  {
    key: "acos",
    title: "ACOS",
    shortTitle: "ACOS",
    type: "number",
    dataIndex: "acos",
    format: "percent",
    maximumFractionDigits: 2,
    minWidth: 90,
    alignment: "right",
    isComparisonMetric: true,
    viewType: ["PERFORMANCE"],
  },
];

export function getTargetingV2Columns(isComparisonView = false) {
  // Hide these columns from the table UI (filters still keep them in ComplexFilter).
  const hiddenColumnKeys = ["match_type", "campaign_type", "campaign_format"];
  const visibleColumns = TARGETING_V2_COLUMN_CONFIG.filter(
    (column) => !hiddenColumnKeys.includes(column.key)
  );

  // Move Status to the first column and ensure it stays sticky.
  const statusCol = visibleColumns.find((c) => c.key === "status");
  const columnsOrdered = [
    statusCol ? { ...statusCol, isSticky: true } : null,
    ...visibleColumns.filter((c) => c.key !== "status"),
  ].filter(Boolean);

  let cumulativeStickyLeft = 0;
  return columnsOrdered.map((column, index) => {
    const minWidth = column.minWidth || 120;
    const isComparisonMetric =
      isComparisonView && !!column.isComparisonMetric;
    const updated = {
      ...column,
      stickyLeft: index === 0 ? 0 : cumulativeStickyLeft,
      colSpan: isComparisonMetric ? 2 : 1,
    };
    if (column.isSticky) {
      cumulativeStickyLeft += minWidth;
    }
    return updated;
  });
}

export const FiltersDimensionDataTargetingV2 = {
  amazon: {
    operators: commonFilterOperators,
    fields: [
      {
        name: "Targeting",
        id: "targeting",
        datatype: "string",
        type: "default",
        placeholder: "Enter value",
        dynamicOptions: false,
        autoComplete: true,
        source: "Adyogi",
        grouping: "Targeting",
      },
      {
        name: "Campaign Name",
        id: "campaign_name",
        datatype: "string",
        type: "default",
        placeholder: "Enter value",
        dynamicOptions: true,
        optionsKey: "campaign_names_api_key",
        source: "BackendAll",
        optionsType: "Static",
        dataFromApi: true,
        apiKey: "campaign_names_api_key",
        backendSearch: true,
        grouping: "Campaign Name",
      },
      {
        name: "Portfolio Name",
        id: "portfolio_name",
        datatype: "string",
        type: "default",
        placeholder: "Enter value",
        dynamicOptions: true,
        optionsKey: "portfolio_names_api_key",
        source: "BackendAll",
        optionsType: "Static",
        dataFromApi: true,
        apiKey: "portfolio_names_api_key",
        backendSearch: true,
        grouping: "Portfolio Name",
      },
      {
        name: "Ad Group Name",
        id: "ad_group_name",
        datatype: "string",
        type: "default",
        placeholder: "Enter value",
        dynamicOptions: true,
        optionsKey: "ad_group_names_api_key",
        source: "BackendAll",
        optionsType: "Static",
        dataFromApi: true,
        apiKey: "ad_group_names_api_key",
        backendSearch: true,
        grouping: "Ad Group Name",
      },
      {
        name: "Product Set",
        id: "product_set",
        datatype: "string",
        type: "default",
        placeholder: "Enter value",
        dynamicOptions: true,
        optionsKey: "product_set_api_key",
        source: "BackendAll",
        optionsType: "Static",
        dataFromApi: true,
        apiKey: "product_set_api_key",
        backendSearch: true,
        grouping: "Product Set",
      },
      {
        name: "Targeting Type",
        id: "targeting_type",
        datatype: "string",
        type: "default",
        placeholder: "Enter value",
        dynamicOptions: false,
        autoComplete: true,
        source: "Adyogi",
        optionsType: "Static",
        optionsKey:
          "G.Keyword,C.Keyword,NB.Keyword,Audience,Automatic,NB.Product,B.Keyword,B.Audience,B.Product",
        grouping: "Targeting Type",
      },
      {
        name: "Match Type",
        id: "match_type",
        datatype: "string",
        type: "default",
        placeholder: "Enter value",
        dynamicOptions: false,
        autoComplete: true,
        source: "Adyogi",
        optionsType: "Static",
        optionsKey: "EXACT,BROAD,PHRASE",
        grouping: "Match Type",
      },
      {
        name: "Campaign Type",
        id: "campaign_type",
        datatype: "string",
        type: "default",
        placeholder: "Enter value",
        dynamicOptions: false,
        autoComplete: true,
        source: "Adyogi",
        optionsType: "Static",
        optionsKey: "SP,SB(V),SB",
        grouping: "Campaign Type",
      },
      {
        name: "Campaign Format",
        id: "campaign_format",
        datatype: "string",
        type: "default",
        placeholder: "Enter value",
        dynamicOptions: false,
        autoComplete: true,
        source: "Adyogi",
        optionsType: "Static",
        optionsKey: "Prospect,Retarget",
        grouping: "Campaign Format",
      },
      {
        name: "Bid Strategy",
        id: "campaign_bid_strategy",
        datatype: "string",
        type: "default",
        placeholder: "Enter value",
        dynamicOptions: false,
        autoComplete: true,
        source: "Adyogi",
        grouping: "Bid Strategy",
      },
      {
        name: "Campaign Status",
        id: "campaign_status",
        datatype: "string",
        type: "default",
        placeholder: "Enter value",
        dynamicOptions: false,
        autoComplete: true,
        source: "Adyogi",
        optionsType: "Static",
        optionsKey: "PAUSED,ENABLED,ARCHIVED",
        grouping: "Campaign Status",
      },
      {
        name: "Status",
        id: "status",
        datatype: "string",
        type: "default",
        placeholder: "Enter value",
        dynamicOptions: false,
        autoComplete: true,
        source: "Adyogi",
        optionsType: "Static",
        optionsKey: "ENABLED,PAUSED",
        grouping: "Status",
      },
      {
        name: "Targeting Added On",
        id: "amz_creation_date_time",
        datatype: "date",
        type: "default",
        placeholder: "Select date",
        dynamicOptions: false,
        autoComplete: false,
        source: "Adyogi",
        grouping: "Targeting Added On",
      },
    ],
  },
};

export const FiltersMeasuresDataTargetingV2 = {
  amazon: {
    operators: commonFilterOperators,
    fields: [
      {
        name: "Spends",
        id: "spends",
        datatype: "int",
        type: "default",
        placeholder: "Enter value",
        dynamicOptions: false,
        autoComplete: true,
        source: "Adyogi",
        grouping: "Spends",
      },
      {
        name: "Impressions",
        id: "impressions",
        datatype: "int",
        type: "default",
        placeholder: "Enter value",
        dynamicOptions: false,
        autoComplete: true,
        source: "Adyogi",
        grouping: "Impressions",
      },
      {
        name: "Clicks",
        id: "clicks",
        datatype: "int",
        type: "default",
        placeholder: "Enter value",
        dynamicOptions: false,
        autoComplete: true,
        source: "Adyogi",
        grouping: "Clicks",
      },
      {
        name: "Orders",
        id: "orders",
        datatype: "int",
        type: "default",
        placeholder: "Enter value",
        dynamicOptions: false,
        autoComplete: true,
        source: "Adyogi",
        grouping: "Orders",
      },
      {
        name: "Revenue",
        id: "revenue",
        datatype: "int",
        type: "default",
        placeholder: "Enter value",
        dynamicOptions: false,
        autoComplete: true,
        source: "Adyogi",
        grouping: "Revenue",
      },
      {
        name: "ROAS",
        id: "roas",
        datatype: "int",
        type: "default",
        placeholder: "Enter value",
        dynamicOptions: false,
        autoComplete: true,
        source: "Adyogi",
        grouping: "ROAS",
      },
      {
        name: "CTR",
        id: "ctr",
        datatype: "int",
        type: "default",
        placeholder: "Enter value",
        dynamicOptions: false,
        autoComplete: true,
        source: "Adyogi",
        grouping: "CTR",
      },
      {
        name: "CPC",
        id: "cpc",
        datatype: "int",
        type: "default",
        placeholder: "Enter value",
        dynamicOptions: false,
        autoComplete: true,
        source: "Adyogi",
        grouping: "CPC",
      },
      {
        name: "CVR",
        id: "cvr",
        datatype: "int",
        type: "default",
        placeholder: "Enter value",
        dynamicOptions: false,
        autoComplete: true,
        source: "Adyogi",
        grouping: "CVR",
      },
      {
        name: "ACOS",
        id: "acos",
        datatype: "int",
        type: "default",
        placeholder: "Enter value",
        dynamicOptions: false,
        autoComplete: true,
        source: "Adyogi",
        grouping: "ACOS",
      },
      {
        name: "Bid",
        id: "bid",
        datatype: "int",
        type: "default",
        placeholder: "Enter value",
        dynamicOptions: false,
        autoComplete: true,
        source: "Adyogi",
        grouping: "Bid",
      },
    ],
  },
};

export const FiltersCampaignDataTargetingV2 = {
  amazon: {
    operators: commonFilterOperators,
    fields: [],
  },
};

/** Top / Bottom % (Apply Global Condition) config for Targeting V2 ComplexFilter. */
export const getTopXDataConfigTargetingV2 = (platform = "amazon") => ({
  operators: {
    int: [
      { id: "TOP", name: "Top", multiple: false },
      { id: "BOTTOM", name: "Bottom", multiple: false },
    ],
  },
  fields: updatePlaceholders(
    FiltersMeasuresDataTargetingV2[platform]?.fields ?? []
  ),
});
